import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  useCertificateHolder,
  useCreateCertificateHolder,
  useUpdateCertificateHolder,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { US_STATES } from "@shared/schema";
import type { CertificateHolder, InsertCertificateHolder } from "@shared/schema";

// CSR-745 T8 + UI redesign: simplified create/edit dialog. The schema
// retains all 17 columns, but the UI now collects only the 6 fields the
// prototype shows (all required): name, email, street, city, state, zip.
// On submit we re-assemble the address into the schema's single text
// column as "street, city, ST 12345". On edit we parse it back the same
// way SendCOIDialog does.

interface CertificateHolderFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  holderId?: string | null;
  onSaved?: (holder: CertificateHolder) => void;
}

interface FormState {
  name: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  email: "",
  street: "",
  city: "",
  state: "",
  zip: "",
};

const STATE_VALUES: readonly string[] = US_STATES.map((s) => s.value);

// Best-effort parse mirroring SendCOIDialog.parseHolderAddress: split on
// commas, then peel off "ST 12345" from the trailing token. Falls back
// to dumping the whole string into `street` if the format isn't
// recognized.
function parseStoredAddress(addr: string | null | undefined): {
  street: string;
  city: string;
  state: string;
  zip: string;
} {
  if (!addr) return { street: "", city: "", state: "", zip: "" };
  const parts = addr
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    const street = parts.slice(0, parts.length - 2).join(", ");
    const city = parts[parts.length - 2];
    const last = parts[parts.length - 1];
    const m = last.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (m) return { street, city, state: m[1], zip: m[2] };
  }
  return { street: addr, city: "", state: "", zip: "" };
}

function fromHolder(holder: CertificateHolder): FormState {
  const parsed = parseStoredAddress(holder.address);
  return {
    name: holder.name ?? "",
    email: holder.contactEmail ?? "",
    street: parsed.street,
    city: parsed.city,
    state: parsed.state,
    zip: parsed.zip,
  };
}

function generateClientId(): string {
  // Browser-friendly UUID slice -- mirrors the existing
  // migrations/0006 ID pattern (`CH-XXXXXXXX`).
  const uuid =
    typeof crypto !== "undefined" && typeof (crypto as Crypto).randomUUID === "function"
      ? (crypto as Crypto).randomUUID()
      : Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
  return `CH-${uuid.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email"),
  street: z.string().trim().min(1, "Street address is required"),
  city: z.string().trim().min(1, "City is required"),
  state: z
    .string()
    .trim()
    .min(1, "State is required")
    .refine((v) => STATE_VALUES.includes(v), "Select a valid US state"),
  zip: z.string().trim().min(1, "Zip is required"),
});

type FieldErrors = Partial<Record<keyof FormState, string>>;

const inputClass = "rounded-lg h-10";

export function CertificateHolderFormDialog({
  open,
  onOpenChange,
  mode,
  holderId,
  onSaved,
}: CertificateHolderFormDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const { data: existing, isLoading: loadingExisting } = useCertificateHolder(
    mode === "edit" ? holderId ?? null : null,
  );
  const createMutation = useCreateCertificateHolder();
  const updateMutation = useUpdateCertificateHolder();

  // Reset form whenever the dialog opens. In edit mode, wait for the
  // single-row fetch to land then prefill from the row.
  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setForm(EMPTY_FORM);
      setErrors({});
    } else if (existing) {
      setForm(fromHolder(existing));
      setErrors({});
    }
  }, [open, mode, existing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Pre-validate to keep the submit button enabled only when every
  // required field has a value. Full Zod validation runs on submit.
  const allFilled = useMemo(
    () =>
      form.name.trim().length > 0 &&
      form.email.trim().length > 0 &&
      form.street.trim().length > 0 &&
      form.city.trim().length > 0 &&
      form.state.trim().length > 0 &&
      form.zip.trim().length > 0,
    [form],
  );
  const canSubmit = allFilled && !isPending;

  const submit = () => {
    const parsed = formSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    const v = parsed.data;
    const composedAddress = `${v.street}, ${v.city}, ${v.state} ${v.zip}`;

    const payload: InsertCertificateHolder = {
      id: mode === "create" ? generateClientId() : (holderId as string),
      name: v.name,
      contactEmail: v.email.toLowerCase(),
      address: composedAddress,
    };

    if (mode === "create") {
      createMutation.mutate(payload, {
        onSuccess: (holder) => {
          toast({ title: "Certificate holder created" });
          onSaved?.(holder);
          onOpenChange(false);
        },
        onError: () => {
          toast({
            title: "Failed to create certificate holder",
            variant: "destructive",
          });
        },
      });
    } else {
      const { id, ...patch } = payload;
      void id;
      updateMutation.mutate(
        { id: holderId as string, data: patch },
        {
          onSuccess: (holder) => {
            toast({ title: "Certificate holder updated" });
            onSaved?.(holder);
            onOpenChange(false);
          },
          onError: () => {
            toast({
              title: "Failed to update certificate holder",
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (canSubmit) submit();
          }
        }}
        data-testid="dialog-certificate-holder-form"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New Certificate Holder" : "Edit Certificate Holder"}
          </DialogTitle>
          {/* CSR-745 prototype match: no subtitle in the dialog header. */}
          <DialogDescription className="sr-only">
            {mode === "create"
              ? "Create a new certificate holder."
              : "Update this certificate holder."}
          </DialogDescription>
        </DialogHeader>

        {mode === "edit" && loadingExisting ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ch-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ch-name"
                autoFocus={mode === "create"}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className={inputClass}
                data-testid="input-holder-name"
              />
              {errors.name && (
                <p className="text-xs text-destructive" data-testid="error-holder-name">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ch-email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ch-email"
                type="email"
                placeholder="name@example.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inputClass}
                data-testid="input-holder-email"
              />
              {errors.email && (
                <p className="text-xs text-destructive" data-testid="error-holder-email">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ch-street">
                Street Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ch-street"
                value={form.street}
                onChange={(e) => set("street", e.target.value)}
                className={inputClass}
                data-testid="input-holder-street"
              />
              {errors.street && (
                <p className="text-xs text-destructive" data-testid="error-holder-street">
                  {errors.street}
                </p>
              )}
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="ch-city">
                  City <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ch-city"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className={inputClass}
                  data-testid="input-holder-city"
                />
                {errors.city && (
                  <p className="text-xs text-destructive" data-testid="error-holder-city">
                    {errors.city}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ch-state">
                  State <span className="text-destructive">*</span>
                </Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger
                    id="ch-state"
                    className="rounded-lg h-10"
                    data-testid="select-holder-state"
                  >
                    <SelectValue placeholder="Select state..." />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.state && (
                  <p className="text-xs text-destructive" data-testid="error-holder-state">
                    {errors.state}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ch-zip">
                  Zip <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ch-zip"
                  value={form.zip}
                  onChange={(e) => set("zip", e.target.value)}
                  className={inputClass}
                  data-testid="input-holder-zip"
                />
                {errors.zip && (
                  <p className="text-xs text-destructive" data-testid="error-holder-zip">
                    {errors.zip}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="button-holder-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full gap-2"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="button-holder-submit"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "create" ? "Create" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
